import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/AddProductFilters";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import { breakpointMediaQueries, getProp } from "@insite/mobius/utilities";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    pagination: getProductListDataViewProperty(state, "pagination"),
});

const mapDispatchToProps = {
    addProductFilters,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductListSortSelectStyles {
    select?: SelectPresentationProps;
}

export const sortSelectStyles: ProductListSortSelectStyles = {
    select: {
        labelProps: {
            css: css`
                padding: 0 !important;
                padding-top: 9px !important;
                width: 110px !important;
            `,
        },
        cssOverrides: {
            formField: css`
                border: 2px solid ${COLORS.background.grayShade};
                border-radius: 40px;
                background: ${COLORS.white} !important;
                width: 225px !important;
                float: right !important;
                label {
                    font-size: ${TYPOGRAPHY.size.smaller} !important;
                    font-weight: ${TYPOGRAPHY.weight.base} !important;
                    line-height: ${TYPOGRAPHY.lineHeight.base} !important;
                    color: ${getProp("theme.typography.p.color")} !important;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            width: 160px !important;
                            margin-right: 12px;
                        `,
                        css`
                            margin-right: 10px;
                            width: 160px !important;
                        `,
                        css`
                            width: auto !important;
                        `,
                        null,
                        null,
                    ])}
            `,
            formInputWrapper: css`
                width: 100% !important;
                select {
                    border: none !important;
                    border-radius: 40px !important;
                    font-size: ${TYPOGRAPHY.size.smaller} !important;
                    font-weight: ${TYPOGRAPHY.weight.bold} !important;
                    line-height: ${TYPOGRAPHY.lineHeight.base} !important;
                    color: ${COLORS.black} !important;
                }
                span {
                    color: ${getProp("theme.typography.p.color")} !important;
                }
            `,
        },
    },
};

const styles = sortSelectStyles;

const ProductListSortSelect = ({ addProductFilters, pagination }: Props) => {
    if (!pagination) {
        return null;
    }

    const onChangeSortHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
        addProductFilters({ sort: event.currentTarget.value });
    };

    return (
        <Select
            {...styles.select}
            label={translate("Sort by:")}
            value={pagination.sortType}
            labelPosition="left"
            preventEllipsis={true}
            onChange={onChangeSortHandler}
            data-test-selector="productListSortSelect"
        >
            {pagination.sortOptions.map(sortOption => (
                <option key={sortOption.sortType} value={sortOption.sortType}>
                    {sortOption.displayName}
                </option>
            ))}
        </Select>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListSortSelect),
    definition: {
        group: "Product List",
        displayName: "Sort Select",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
